import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NarcoticPrescriptionBlockService } from '@app/shared/services/NarcoticPrescriptionBlock.service';
import { AppConsts } from '@shared/AppConsts';
import { DateTime } from 'luxon';

@Injectable()
export class CustomValidationService {

    public emailValidator(control: FormControl) {
        if (control.value) {
            const error = { 'invalidEmail': true };

            if (control.value.length === 0) {
                return error;
            }

            const regexp = new RegExp('^(?:[A-Za-z0-9!#$%&\'*+\/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&\'*+\/=?^_`{|}~-]+)*|\"(?:[\\x01-\\x08\\x0b\\x0c\\x0e-\\x1f\\x21\\x23-'
            + '\\x5b\\x5d-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])*\")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:'
            + '(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\\x01-\\x08\\x0b'
            + '\\x0c\\x0e-\\x1f\\x21-\\x5a\\x53-\\x7f]|\\\\[\\x01-\\x09\\x0b\\x0c\\x0e-\\x7f])+)\\])$');

            const match = regexp.test(control.value);

            if (!match) {
                return error;
            }
        }

        return null;
    }

    public phoneValidator(control: FormControl) {
        if (control.value) {
            const error = { 'invalidPhone': true };

            if (control.value.length === 0) {
                return error;
            }

            const regexp = new RegExp('^(\\b(0041|0)|\\B\\+41)(\\s?\\(0\\))?(\\s)?[1-9]{2}(\\s)?[0-9]{3}(\\s)?[0-9]{2}(\\s)?[0-9]{2}\\b$');

            const match = regexp.test(control.value);

            if (!match) {
                return error;
            }
        }

        return null;
    }

    public phoneValidatorInternational(control: FormControl) {
        if (control.value) {
            const error = { 'invalidPhone': true };

            if (control.value.length === 0) {
                return error;
            }

            const regexp = new RegExp('(^\\+(9[976]\\d|8[987530]\\d|6[987]\\d|5[90]\\d|42\\d|3[875]\\d|2[98654321]\\d|9[8543210]|8[6421]|6[6543210]|5[87654321]|4[987654310]|3[9643210]|2[70]|7|1)\\d{1,14}$)|(^(\\b(0041|0)|\\B\\+41)(\\s?\\(0\\))?(\\s)?(?:[1-9]{2}(\\s)?[0-9]{3}(\\s)?[0-9]{2}(\\s)?[0-9]{2}|(?:800|84[0248])(\\s)?[0-9]{3}(\\s)?[0-9]{3})\\b$)');

            const match = regexp.test(control.value);

            if (!match) {
                return error;
            }
        }

        return null;
    }

    public filenameValidator(control: FormControl) {
        if (control.value) {
            const error = { 'invalidFilename': true };

            if (control.value.length === 0) {
                return error;
            }

            // <  >  :  "  /  \  |  ? are not allowed in windows filenames
            const forbiddenRegex = /[<>:"/\\|?]/;
            const match = forbiddenRegex.test(control.value);

            if (match) {
                return error;
            }
        }

        return null;
    }

    public ahvValidator(control: FormControl) {
        if (control.value) {
            const error = { 'invalidAhv': true };

            if (control.value.length === 0) {
                return error;
            }

            const regexp = new RegExp(`^756\\.?[0-9]{4}\\.?[0-9]{4}\\.?[0-9]{2}$`);

            const match = regexp.test(control.value);

            if (!match) {
                return error;
            }
        }

        return null;
    }

    public ahvValidatorInclForeignAhv(control: FormControl) {
        // This validator is used in person dossier where we want to allow also
        // foreign AHV numbers with 4 to 13 digits without the 756 prefix
        if (control.value) {
            const error = { 'invalidAhv': true };

            if (control.value.length === 0) {
                return error;
            }

            const regexp = new RegExp('^(756\\.\\d{4}\\.\\d{4}\\.\\d{2}|[A-Za-z0-9 ]{4,16})$');

            const match = regexp.test(control.value);

            if (!match) {
                return error;
            }
        }

        return null;
    }

    public glnValidator(control: FormControl) {
        if (control.value) {
            const error = { 'invalidGln': true };

            let gln: number = +control.value;
            if (control.value.length !== 13 || isNaN(gln)) {
                return error;
            }

            const check = gln % 10;

            let sum = 0;
            for (let i = 0; i < 6; i++) {
                gln = Math.floor(gln / 10);
                sum = sum + (gln % 10) * 3;
                gln = Math.floor(gln / 10);
                sum = sum + (gln % 10) * 1;
            }

            const result = (300 - sum) % 10;

            if (result !== check) {
                return error;
            }
        }

        return null;
    }

    public uidValidator(control: FormControl) {
        let uid: string = control.value;
        if (uid) {
            const error = { 'invalidUid': true };

            const regexp = new RegExp(/^(CHE)[-]?\d{3}[.]?\d{3}[.]?\d{3}$/);
            const match = regexp.test(uid);
            if (!match) {
                return error;
            }

            uid = uid.replace('-', '');
            uid = uid.replace('.', '');
            uid = uid.replace('.', ''); // Replace removes only one dot

            // Calculate the checksum
            let multiplicatedDigits: number[] = [];

            multiplicatedDigits.push(Number(uid.charAt(3)) * 5);
            multiplicatedDigits.push(Number(uid.charAt(4)) * 4);
            multiplicatedDigits.push(Number(uid.charAt(5)) * 3);

            multiplicatedDigits.push(Number(uid.charAt(6))  * 2);
            multiplicatedDigits.push(Number(uid.charAt(7))  * 7);
            multiplicatedDigits.push(Number(uid.charAt(8)) * 6);

            multiplicatedDigits.push(Number(uid.charAt(9)) * 5);
            multiplicatedDigits.push(Number(uid.charAt(10)) * 4);
            let checkDigit = Number(uid.charAt(11));

            let sum = multiplicatedDigits.reduce((partialSum, a) => partialSum + a, 0);
            let moduloRest = sum % 11;

            if (moduloRest == 0) {
                if (checkDigit != 0) {
                    return error;
                }
            } else {
                if ((11 - moduloRest) != checkDigit) {
                    return error;
                }
            }
        }

        return null;
    }

    public getPostCodeValidators(notRequired: boolean = false, isCountrySwitzerland: boolean = true): ValidatorFn[] {
        let validators = [Validators.maxLength(10)];

        if (isCountrySwitzerland) {
            validators = [Validators.minLength(4),
                Validators.maxLength(4),
                Validators.pattern('^[0-9]*$')];
        }

        if (!notRequired) {
            validators.push(Validators.required);
        }

        return validators;
    }

    public numbersOnlyValidation(event) {
        const inp = String.fromCharCode(event.keyCode);
        /*Allow only numbers*/
        if (!/^\d+$/.test(inp)) {
            event.preventDefault();
        }
    }

    public narcoticPrescriptionBlockFirstRecipeNumberValidator(narcoticPrescriptionBlockService: NarcoticPrescriptionBlockService): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (control.value) {
                return narcoticPrescriptionBlockService.ValidateFirstRecipeNumber(control.value);
            }

            return null;
        }
    }

    public countryMustBeSwitzerlandValidator(control: FormControl) {
        if (control.value) {
            const error = { 'countryMustBeSwitzerland': true };

            if (control.value != AppConsts.codes.country.ch) {
                return error;
            }
        }

        return null;
    }
    public guidValidator(control: FormControl) {
        if (control.value) {
            const error = { 'invalidGuid': true };

            if (control.value.length === 0) {
                return error;
            }

            const regexp = new RegExp('^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$', 'i');
            const match = regexp.test(control.value);

            if (!match) {
                return error;
            }
        }

        return null;
    }

    public onlyWhitespaceValidator(control: FormControl) {
        if (control.value) {
            const error = { 'onlyWhitespace': true };

            // Normalize value to string (handles p-autoComplete returning objects)
            let text: string;
            if (typeof control.value === 'string') {
                text = control.value;
            } else if (typeof control.value === 'object') {
                text = (control.value as any).label ?? (control.value as any).name ?? JSON.stringify(control.value);
            } else {
                text = String(control.value);
            }

            if (text.length === 0) {
                return error;
            }

            if (text.trim().length === 0) {
                return error;
            }
        }

        return null;
    }

    public dateMustBeInPastOrTodayValidator = (control: FormControl): ValidationErrors | null => {
        const startDate = control.value;
        if (!startDate) {
          return null;
        }

        const today = DateTime.now().startOf('day');
        const date = DateTime.fromISO(startDate).startOf('day');

        return date <= today ? null : { dateMustBeInPastOrToday: true } ;
      };


    public ibanValidator(control: FormControl) {
        if (control.value) {
            const error = { 'invalidIban': true };
            const iban = control.value.replace(/[\s]/g, ''); // Remove spaces

            if (iban.length < 15 || iban.length > 34) {
                return error;
            }

            // Move first 4 characters to end
            const rearranged = iban.substring(4) + iban.substring(0, 4);

            // Replace letters with numbers (A=10, B=11, ..., Z=35)
            let numericString = '';
            for (let i = 0; i < rearranged.length; i++) {
                const char = rearranged.charAt(i);
                if (char >= 'A' && char <= 'Z') {
                    numericString += (char.charCodeAt(0) - 55).toString();
                } else {
                    numericString += char;
                }
            }

            // Calculate MOD97
            let remainder = numericString;
            while (remainder.length > 2) {
                const block = remainder.substring(0, 9);
                remainder = (parseInt(block, 10) % 97).toString() + remainder.substring(block.length);
            }

            if (parseInt(remainder, 10) % 97 !== 1) {
                return error;
            }
        }

        return null;
    }

    public semesterValidator(control: FormControl) {
        if (control.value) {
            const error = { 'invalidSemester': true };
            const regexp = new RegExp(/^(HS|FS)\d{4}$/);
            const match = regexp.test(control.value);
            if (!match) {
                return error;
            }
        }
        return null;
    }
}

